import React, { useMemo } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    Cell,
} from 'recharts';
import {
    TrendingUp,
    TrendingDown,
    ShowChart,
    CalendarMonth,
    EmojiEvents,
} from '@mui/icons-material';

const MONTH_LABELS = {
    Jan: 'Janvier',
    Fev: 'Février',
    Feb: 'Février',
    Mar: 'Mars',
    Avr: 'Avril',
    Apr: 'Avril',
    Mai: 'Mai',
    May: 'Mai',
    Jun: 'Juin',
    Jui: 'Juillet',
    Jul: 'Juillet',
    Aut: 'Août',
    Aug: 'Août',
    Sep: 'Septembre',
    Oct: 'Octobre',
    Nov: 'Novembre',
    Dec: 'Décembre',
};

const formatAxisAmount = (num, currency) => {
    const n = Number(num) || 0;
    if (n >= 1_000_000_000) return `${currency === 'USD' ? '$' : ''}${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `${currency === 'USD' ? '$' : ''}${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${currency === 'USD' ? '$' : ''}${(n / 1_000).toFixed(1)}K`;
    return currency === 'USD' ? `$${n.toLocaleString('fr-FR')}` : `${n.toLocaleString('fr-FR')}`;
};

const formatFullAmount = (num, currency) =>
    currency === 'USD'
        ? `$${Number(num).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}`
        : `${Number(num).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} CDF`;

const monthFullName = (short) => MONTH_LABELS[short] || short;

const calculateAugmentation = (currentValue, lastValue) => {
    const cur = currentValue ?? 0;
    const last = lastValue ?? 0;
    if (last === 0 && cur === 0) return 0;
    if (last === 0 && cur > 0) return 100;
    return ((cur - last) / last) * 100;
};

const KpiPill = ({ icon: Icon, label, value, sub, accent = 'amber' }) => {
    const accents = {
        amber: 'border-amber-200/80 bg-amber-50/60 text-amber-800',
        slate: 'border-slate-200/80 bg-slate-50/80 text-slate-800',
        emerald: 'border-emerald-200/80 bg-emerald-50/60 text-emerald-800',
    };
    return (
        <div
            className={`flex min-w-0 flex-1 flex-col gap-1 rounded-xl border px-4 py-3 transition-shadow duration-300 hover:shadow-md ${accents[accent] || accents.amber}`}
        >
            <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/80 shadow-sm">
                    <Icon className="text-base opacity-90" aria-hidden />
                </span>
                <p className="truncate text-[11px] font-semibold uppercase tracking-wide opacity-75">{label}</p>
            </div>
            <p className="truncate text-lg font-bold tabular-nums text-slate-900" title={value}>
                {value}
            </p>
            {sub && <p className="truncate text-xs text-slate-600">{sub}</p>}
        </div>
    );
};

const ChartTooltip = ({ active, payload, currency }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    const amount = point?.amount ?? 0;
    const isPeak = point?.isPeak;

    return (
        <div
            className="min-w-[168px] rounded-xl border border-slate-200/90 bg-white/95 p-3 shadow-xl backdrop-blur-sm"
            role="tooltip"
        >
            <p className="text-xs font-medium text-slate-500">{monthFullName(point.name)}</p>
            <p className="mt-1 text-xl font-bold tabular-nums text-slate-900">
                {formatFullAmount(amount, currency)}
            </p>
            {isPeak && (
                <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                    <EmojiEvents className="!text-xs" />
                    Meilleur mois
                </p>
            )}
        </div>
    );
};

const ChartSkeleton = () => (
    <div className="animate-pulse space-y-6 p-6">
        <div className="flex gap-4">
            <div className="h-10 w-10 rounded-xl bg-slate-200" />
            <div className="flex-1 space-y-2">
                <div className="h-5 w-48 rounded-lg bg-slate-200" />
                <div className="h-3 w-64 rounded bg-slate-100" />
            </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 rounded-xl bg-slate-100" />
            ))}
        </div>
        <div className="flex h-72 items-end justify-between gap-2 rounded-xl bg-slate-50 px-4 pb-4 pt-8">
            {Array.from({ length: 8 }).map((_, i) => (
                <div
                    key={i}
                    className="w-full max-w-[40px] rounded-t-lg bg-slate-200"
                    style={{ height: `${30 + (i % 4) * 18}%` }}
                />
            ))}
        </div>
    </div>
);

const AdminSalesChart = ({ data, currency, loading = false, apiDashboard, chartYear }) => {
    const currentCalendarYear = new Date().getFullYear();
    const isCurrentYear = chartYear == null || chartYear === currentCalendarYear;

    const chartData = useMemo(() => {
        if (!data?.labels?.length) return [];
        const rows = data.labels.map((label, index) => ({
            name: label,
            amount: Number(data.values[index]) || 0,
        }));
        const maxAmount = Math.max(0, ...rows.map((r) => r.amount));
        return rows.map((r) => ({
            ...r,
            isPeak: maxAmount > 0 && r.amount === maxAmount,
        }));
    }, [data]);

    const stats = useMemo(() => {
        if (!chartData.length) {
            return { total: 0, average: 0, peak: null, peakAmount: 0 };
        }
        const total = chartData.reduce((s, d) => s + d.amount, 0);
        const average = total / chartData.length;
        const peak = chartData.reduce((best, d) => (d.amount > best.amount ? d : best), chartData[0]);
        return { total, average, peak, peakAmount: peak.amount };
    }, [chartData]);

    const chartMax = useMemo(() => {
        const m = Math.max(stats.peakAmount, stats.average, 0);
        return m * 1.15 || 1;
    }, [stats.peakAmount, stats.average]);

    const yearlyAmount = apiDashboard?.yearlyAmount ?? 0;
    const lastYearAmount = apiDashboard?.lastYearAmount ?? 0;
    const evolutionPct = calculateAugmentation(yearlyAmount, lastYearAmount);
    const trend =
        yearlyAmount > lastYearAmount ? 'up' : yearlyAmount < lastYearAmount ? 'down' : 'neutral';
    const evolutionLabel =
        trend === 'up' ? `+${evolutionPct.toFixed(0)}%` : evolutionPct === 0 ? '0%' : `${evolutionPct.toFixed(0)}%`;

    const barColors = useMemo(
        () =>
            chartData.map((d) =>
                d.isPeak
                    ? 'url(#barPeak)'
                    : d.amount > 0
                    ? 'url(#barDefault)'
                    : '#e2e8f0'
            ),
        [chartData]
    );

    const chartSummary = `Entrées mensuelles ${chartYear ?? currentCalendarYear} en ${currency}. Total ${formatFullAmount(stats.total, currency)}.`;

    if (loading) {
        return (
            <section
                className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm"
                aria-busy="true"
                aria-label="Chargement du graphique des entrées"
            >
                <ChartSkeleton />
            </section>
        );
    }

    if (!chartData.length) {
        return (
            <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm">
                <div className="flex h-72 flex-col items-center justify-center text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50">
                        <ShowChart className="text-4xl text-amber-400" aria-hidden />
                    </div>
                    <p className="text-lg font-semibold text-slate-800">Pas encore de données</p>
                    <p className="mt-2 max-w-sm text-sm text-slate-500">
                        Les entrées mensuelles s&apos;afficheront ici dès que votre organisation enregistrera des
                        transactions.
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section
            className="group overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-shadow duration-300 hover:shadow-md"
            aria-label={chartSummary}
        >
            {/* En-tête */}
            <div className="border-b border-slate-100 bg-gradient-to-r from-white via-amber-50/30 to-white px-6 py-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/25 transition-transform duration-300 group-hover:scale-105">
                            <ShowChart className="text-2xl text-white" aria-hidden />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                                Entrées mensuelles
                            </h2>
                            <p className="mt-1 text-sm text-slate-600">
                                Répartition par mois · {currency} · Année {chartYear ?? currentCalendarYear}
                            </p>
                        </div>
                    </div>

                    {isCurrentYear && (
                        <div
                            className={`inline-flex items-center gap-2 self-start rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                                trend === 'up'
                                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80'
                                    : trend === 'down'
                                    ? 'bg-red-50 text-red-700 ring-1 ring-red-200/80'
                                    : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200/80'
                            }`}
                            title="Évolution du cumul annuel par rapport à l'année précédente"
                        >
                            {trend === 'down' ? (
                                <TrendingDown className="text-lg" aria-hidden />
                            ) : (
                                <TrendingUp className={`text-lg ${trend === 'neutral' ? 'opacity-60' : ''}`} aria-hidden />
                            )}
                            <span className="tabular-nums">{evolutionLabel}</span>
                            <span className="text-xs font-normal opacity-75">vs N-1</span>
                        </div>
                    )}
                </div>

                {/* KPIs */}
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <KpiPill
                        icon={CalendarMonth}
                        label="Total période"
                        value={formatFullAmount(stats.total, currency)}
                        sub={`${chartData.length} mois affichés`}
                        accent="amber"
                    />
                    <KpiPill
                        icon={ShowChart}
                        label="Moyenne / mois"
                        value={formatFullAmount(stats.average, currency)}
                        sub="Ligne pointillée sur le graphique"
                        accent="slate"
                    />
                    <KpiPill
                        icon={EmojiEvents}
                        label="Meilleur mois"
                        value={stats.peak ? formatFullAmount(stats.peakAmount, currency) : '—'}
                        sub={stats.peak ? monthFullName(stats.peak.name) : '—'}
                        accent="emerald"
                    />
                </div>
            </div>

            {/* Graphique */}
            <div className="px-4 py-6 sm:px-6">
                <div className="relative rounded-xl bg-slate-50/50 p-2 ring-1 ring-slate-100 sm:p-4">
                    <div className="h-72 w-full sm:h-80" role="img" aria-label={chartSummary}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={{ top: 16, right: 8, left: 4, bottom: 8 }}
                                barCategoryGap="18%"
                            >
                                <defs>
                                    <linearGradient id="barDefault" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#fbbf24" />
                                        <stop offset="100%" stopColor="#f59e0b" />
                                    </linearGradient>
                                    <linearGradient id="barPeak" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#ea580c" />
                                        <stop offset="100%" stopColor="#c2410c" />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="4 4"
                                    stroke="#e2e8f0"
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                                    dy={8}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    width={48}
                                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                                    domain={[0, chartMax]}
                                    tickFormatter={(v) => formatAxisAmount(v, currency)}
                                />
                                <Tooltip
                                    content={<ChartTooltip currency={currency} />}
                                    cursor={{ fill: 'rgba(251, 191, 36, 0.08)', radius: 8 }}
                                />
                                {stats.average > 0 && (
                                    <ReferenceLine
                                        y={stats.average}
                                        stroke="#94a3b8"
                                        strokeDasharray="6 4"
                                        strokeWidth={1.5}
                                        label={{
                                            value: 'Moyenne',
                                            position: 'insideTopRight',
                                            fill: '#64748b',
                                            fontSize: 10,
                                            offset: 8,
                                        }}
                                    />
                                )}
                                <Bar
                                    dataKey="amount"
                                    radius={[8, 8, 0, 0]}
                                    maxBarSize={48}
                                    animationDuration={700}
                                    animationEasing="ease-out"
                                >
                                    {chartData.map((_, index) => (
                                        <Cell key={`cell-${index}`} fill={barColors[index]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
                        <span className="inline-flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-sm bg-gradient-to-b from-amber-400 to-amber-500" />
                            Entrées du mois
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-sm bg-gradient-to-b from-orange-600 to-orange-700" />
                            Pic mensuel
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <span className="h-4 w-6 border-t-2 border-dashed border-slate-400" />
                            Moyenne
                        </span>
                    </div>
                    <p className="text-xs text-slate-500">
                        Transactions entrantes validées · {currency}
                    </p>
                </div>
            </div>
        </section>
    );
};

export default AdminSalesChart;
